import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { INK, INDIGO, SUB, FAINT, AMBER, HAIR, SOFT, PANEL, GROUPS, CLAIM_ST, ADV_FIELDS, advErrors } from "./erShared";

const SF_CSS = `.er-sfrow{transition:background-color .1s}.er-sfrow:hover{background:#F1F1F4}
.er-sffacet:hover{background:#F1F1F4}
.er-sf-pane input[type=radio],.er-sf-pane input[type=checkbox]{accent-color:#141412}`;

export function SegSwitch({ items, value, onChange, testid }) {
  return (
    <div role="radiogroup" className="inline-flex items-center rounded-[10px] p-[3px]" style={{ background: SOFT }} data-testid={testid}>
      {items.map(([k, label]) => {
        const sel = value === k;
        return <button key={k} type="button" role="radio" aria-checked={sel} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className="text-[13px] font-semibold px-3 py-[6px] rounded-[7px]" style={{ background: sel ? "#fff" : "transparent", color: sel ? INK : SUB, boxShadow: sel ? "0 1px 2px rgba(20,20,18,.1)" : "none" }}>{label}</button>;
      })}
    </div>
  );
}

function Radios({ name, list, value, onChange, allLabel }) {
  const opts = allLabel != null ? [["", allLabel], ...list] : list;
  return opts.map(([v, l, note]) => (
    <label key={v || "_all"} className="flex items-center gap-2 py-[5px] text-[13px] cursor-pointer" style={{ color: INK }}>
      <input type="radio" name={name} checked={(value || "") === v} onChange={() => onChange(v)} className="accent-[#141412]" data-testid={`er-sf-${name}-${v || "all"}`} />
      <span className="truncate">{l}</span>{note ? <span className="text-[11.5px]" style={{ color: FAINT }}>{note}</span> : null}
    </label>
  ));
}

const H = ({ children, mt }) => <div className="text-[11px] font-bold uppercase tracking-[.06em] mb-1" style={{ color: FAINT, marginTop: mt ? 14 : 0 }}>{children}</div>;

const SORTS = [["added", "Newest added"], ["dos_new", "Newest date of service"], ["dos_old", "Oldest date of service"], ["wait", "Waiting longest", "this page"]];
const FACETS = [["sortgroup", "Sort & group"], ["provider", "Physician"], ["tov", "Type of visit"], ["payer", "Payer"], ["status", "Claim status"]];
const tovLabel = (v) => (String(v).toUpperCase() === "NOT SET" ? "Not set" : v);

function OptRow({ name, value, label, note, count, checked, onChange, dot }) {
  return (
    <label className="er-sfrow flex items-center gap-2 px-2.5 py-2 rounded-[8px] cursor-pointer" style={{ background: checked ? SOFT : "transparent" }} data-testid={`er-sf-${name}-${value || "all"}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="accent-[#141412] flex-none" />
      {dot ? <span className="w-[6px] h-[6px] rounded-full flex-none" style={{ background: dot }} /> : null}
      <span className="truncate flex-1 text-[13px]" style={{ color: INK, fontWeight: checked ? 600 : 400 }}>{label}{note ? <span className="ml-1 text-[11.5px]" style={{ color: FAINT }}>{note}</span> : null}</span>
      {count != null ? <span className="flex-none text-[12px] font-semibold tabular-nums" style={{ color: SUB }}>{count}</span> : null}
    </label>
  );
}

function OptionList({ name, allLabel, noun, options, value, onChange, countFor, pinned }) {
  const [q, setQ] = useState("");
  const showSearch = options.length > 6;
  const ql = q.trim().toLowerCase();
  const filtered = ql ? options.filter(([, l]) => String(l).toLowerCase().includes(ql)) : options;
  return (
    <div className="flex flex-col h-full min-h-0">
      {showSearch ? (
        <div className="relative mb-1.5 flex-none">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={FAINT} strokeWidth="1.6" className="absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="M11 11l3 3" strokeLinecap="round" /></svg>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${noun}`} className="w-full text-[13px] pl-8 pr-2 h-[34px] rounded-[9px] outline-none bg-white focus:border-[#141412]" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid={`er-sf-search-${name}`} />
        </div>
      ) : null}
      <div className="flex-1 min-h-0 overflow-y-auto pr-0.5 er-sf-list" data-testid={`er-sf-list-${name}`}>
        {!ql ? <OptRow name={name} value="" label={allLabel} count={countFor(name, "")} checked={!value} onChange={() => onChange("")} /> : null}
        {pinned}
        {filtered.length ? filtered.map(([v, l]) => (
          <OptRow key={v} name={name} value={v} label={name === "tov" ? tovLabel(l) : l} count={countFor(name, v)} checked={(value || "") === v} onChange={() => onChange(v)} />
        )) : <div className="px-2.5 py-3 text-[12.5px]" style={{ color: FAINT }} data-testid={`er-sf-empty-${name}`}>No {noun} matches &ldquo;{q.trim()}&rdquo;</div>}
      </div>
    </div>
  );
}

function SortGroupPane({ f, setF, more }) {
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const locOn = more ? (more.locOverride == null ? more.multiLoc : more.locOverride) : false;
  return (
    <div className="h-full overflow-y-auto">
      <div className="grid grid-cols-2 gap-5">
        <div><H>Sort by</H><Radios name="sort" list={SORTS} value={f.sort} onChange={set("sort")} /></div>
        <div><H>Group by</H><Radios name="group" list={GROUPS} value={f.group} onChange={set("group")} /></div>
      </div>
      {more ? (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${SOFT}` }}>
          <H>More columns</H>
          <label className="flex items-center gap-2 py-[5px] text-[13px] cursor-pointer" style={{ color: INK }}>
            <input type="checkbox" checked={!!locOn} onChange={(e) => more.setLocOverride(e.target.checked)} className="accent-[#141412]" data-testid="er-sf-col-loc" />
            <span>Location</span>
            {more.locOverride == null && more.multiLoc ? <span className="text-[11.5px]" style={{ color: FAINT }}>auto · {more.nLocations} location{more.nLocations === 1 ? "" : "s"}</span> : null}
          </label>
          {more.sourceAll ? (
            <label className="flex items-center gap-2 py-[5px] text-[13px] cursor-pointer" style={{ color: INK }}>
              <input type="checkbox" checked={!!more.srcCol} onChange={(e) => more.setSrcCol(e.target.checked)} className="accent-[#141412]" data-testid="er-sf-col-source" />
              <span>Source as its own column</span>
            </label>
          ) : null}
          <p className="text-[11.5px] mt-1.5" style={{ color: FAINT }}>Location turns on by itself when this client has more than one location. Shown when there&apos;s room; always in the expanded row.</p>
        </div>
      ) : null}
    </div>
  );
}

function FilterTab({ f, setF, opts, more, countFor, facet, setFacet }) {
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const physLabel = (opts.physicians.find(([v]) => v === f.provider) || [])[1];
  const curVal = (k) => {
    if (k === "sortgroup") { const sv = (SORTS.find(([v]) => v === f.sort) || SORTS[0])[1]; const gv = f.group !== "none" ? ` · ${(GROUPS.find(([v]) => v === f.group) || [])[1]}` : ""; return { val: `${sv}${gv}`, active: f.sort !== "added" || f.group !== "none" }; }
    if (k === "provider") return { val: f.provider ? (physLabel || "Physician") : "All physicians", active: !!f.provider };
    if (k === "tov") return { val: f.tov ? tovLabel(f.tov) : "All types", active: !!f.tov };
    if (k === "payer") return { val: f.payer === "__needs_id__" ? "Insurance to fix" : (f.payer || "All payers"), active: !!f.payer };
    return { val: f.status ? (CLAIM_ST.find(([v]) => v === f.status) || [])[1] : "Any", active: !!f.status };
  };
  const payerPinned = (
    <>
      <OptRow name="payer" value="__needs_id__" label="Insurance to fix" note="all dates" dot="#FF9500" count={countFor("payer", "__needs_id__")} checked={f.payer === "__needs_id__"} onChange={() => set("payer")("__needs_id__")} />
      <div style={{ height: 1, background: HAIR, margin: "2px 6px 4px" }} />
    </>
  );
  return (
    <div className="flex er-sf-pane" style={{ height: 380 }}>
      <div className="flex-none w-[220px] overflow-y-auto p-2" style={{ borderRight: `1px solid ${HAIR}`, background: PANEL }} data-testid="er-sf-rail">
        {FACETS.map(([k, nm]) => {
          const { val, active } = curVal(k), sel = facet === k;
          return (
            <button key={k} type="button" onClick={() => setFacet(k)} className="er-sffacet w-full flex items-center gap-2 text-left px-2.5 py-2 rounded-[9px] mb-0.5"
              style={sel ? { background: "#fff", boxShadow: "0 1px 2px rgba(20,20,18,.08)", border: `1px solid ${HAIR}` } : { border: "1px solid transparent" }} data-testid={`er-sf-facet-${k}`}>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold" style={{ color: INK }}>{nm}</span>
                <span className="flex items-center gap-1 text-[12px]" style={{ color: active ? INK : SUB, fontWeight: active ? 600 : 400 }}>
                  {active ? <span className="w-[6px] h-[6px] rounded-full flex-none" style={{ background: INK }} /> : null}
                  <span className="truncate">{val}</span>
                </span>
              </span>
              <ChevronRight size={14} style={{ color: FAINT }} className="flex-none" />
            </button>
          );
        })}
      </div>
      <div className="flex-1 min-w-0" style={{ padding: "10px 12px" }}>
        {facet === "sortgroup" ? <SortGroupPane f={f} setF={setF} more={more} />
          : facet === "provider" ? <OptionList name="provider" allLabel="All physicians" noun="physician" options={opts.physicians} value={f.provider} onChange={set("provider")} countFor={countFor} />
          : facet === "tov" ? <OptionList name="tov" allLabel="All types" noun="type" options={opts.tovs} value={f.tov} onChange={set("tov")} countFor={countFor} />
          : facet === "payer" ? <OptionList name="payer" allLabel="All payers" noun="payer" options={opts.payers} value={f.payer} onChange={set("payer")} countFor={countFor} pinned={payerPinned} />
          : <OptionList name="status" allLabel="Any" noun="status" options={CLAIM_ST} value={f.status} onChange={set("status")} countFor={countFor} />}
      </div>
    </div>
  );
}

function SearchTab({ draft, setDraft }) {
  const errs = advErrors(draft);
  return (
    <div className="grid grid-cols-3 gap-x-4 gap-y-3 px-[18px] py-4">
      {ADV_FIELDS.map(([k, label, ph]) => (
        <div key={k}>
          <label htmlFor={`er-adv-${k}`} className="block text-[12px] font-semibold mb-1" style={{ color: SUB }}>{label}</label>
          <input id={`er-adv-${k}`} value={draft[k] || ""} placeholder={ph} autoComplete="off" onChange={(e) => setDraft((s) => ({ ...s, [k]: e.target.value }))}
            className="w-full text-[13px] px-3 h-[34px] rounded-[9px] outline-none bg-white focus:border-[#141412]" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid={`er-adv-${k}`} />
          {errs[k] ? <span className="block text-[11.5px] mt-1" style={{ color: AMBER }} data-testid={`er-adv-err-${k}`}>{errs[k]}</span> : null}
        </div>
      ))}
    </div>
  );
}

const BTN = "text-[13px] font-semibold px-3.5 h-[34px] rounded-[9px]";

export function ERSortFilter({ open, setOpen, tab, setTab, f, setF, opts, total, onClearFilters, adv, onSearch, activeCount, children, more, countFor, inGroup }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState({});
  const [facet, setFacet] = useState("sortgroup");
  useEffect(() => { if (open && tab === "search") setDraft(adv); }, [open, tab, adv]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, setOpen]);
  const filled = Object.fromEntries(Object.entries(draft).filter(([, v]) => v && v.trim()).map(([k, v]) => [k, v.trim()]));
  const canSearch = Object.keys(filled).length > 0 && Object.keys(advErrors(draft)).length === 0;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="inline-flex items-center justify-center gap-1.5" title="Sort & Filter" aria-label="Sort & Filter" style={{ height: inGroup ? 36 : 34, minWidth: inGroup ? 40 : 36, padding: activeCount ? "0 9px" : 0, background: activeCount ? SOFT : (inGroup ? "transparent" : "#fff"), color: INK, border: inGroup ? "none" : `1px solid ${HAIR}`, borderRadius: inGroup ? 0 : 9 }} data-testid="er-sf-btn">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M2 4h12M4 8h8M6 12h4" /></svg>
        {activeCount ? <span className="inline-flex items-center justify-center text-[9.5px] font-bold tabular-nums" style={{ height: 16, minWidth: 16, padding: "0 4px", borderRadius: 5, background: INK, color: "#fff" }} data-testid="er-sf-count">{activeCount}</span> : null}
      </button>
      {open ? (
        <div className="absolute right-0 z-40 mt-1 w-[720px] max-w-[calc(100vw-32px)]" style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, boxShadow: "0 12px 32px rgba(20,20,18,.16)" }} data-testid="er-sf-panel">
          <style>{SF_CSS}</style>
          <div style={{ padding: "14px 18px 12px", borderBottom: `1px solid ${SOFT}` }}>
            <SegSwitch items={[["filter", "Sort & filter"], ["search", "Advanced search"]]} value={tab} onChange={setTab} testid="er-sf-tab" />
          </div>
          {tab === "filter" ? <FilterTab f={f} setF={setF} opts={opts} more={more} countFor={countFor} facet={facet} setFacet={setFacet} /> : <SearchTab draft={draft} setDraft={setDraft} />}
          <div className="flex items-center gap-3 px-[18px] py-3" style={{ borderTop: `1px solid ${SOFT}` }}>
            {tab === "filter" ? (
              <>
                <button onClick={onClearFilters} className="text-[13px] font-semibold" style={{ color: INDIGO }} data-testid="er-sf-clear">Clear filters</button>
                <span className="flex-1" />
                <button onClick={() => setOpen(false)} className={BTN} style={{ background: INK, color: "#fff" }} data-testid="er-sf-done">Show {total} encounters</button>
              </>
            ) : (
              <>
                <span className="text-[12px] max-w-[380px]" style={{ color: FAINT }}>Searches every encounter for this client. Filled fields must all match, along with the status and date range above.</span>
                <span className="flex-1" />
                <button onClick={() => setDraft({})} className="text-[13px] font-semibold" style={{ color: INDIGO }} data-testid="er-adv-clear">Clear</button>
                <button onClick={() => setOpen(false)} className={BTN} style={{ border: `1px solid ${HAIR}`, color: INK, background: "#fff" }} data-testid="er-adv-cancel">Cancel</button>
                <button disabled={!canSearch} onClick={() => { onSearch(filled); setOpen(false); }} className={`${BTN} disabled:opacity-40`} style={{ background: INK, color: "#fff" }} data-testid="er-adv-search">Search</button>
              </>
            )}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}
